import dayjs from 'dayjs';
import { Filter as GlobalFilter } from 'lib/common/GlobalSearcher';
import { PURCHASE_ORDER_STATES, TENDER_STATES } from 'lib/consts';
import { PurchaseOrderType, TenderType } from 'lib/types/models';
import { useMemo, useState } from 'react';

interface UsePublicMarketFiltersProps {
  activeTab: number;
  supplierFilterMode: 'tender' | 'purchaseOrder';
}

export const usePublicMarketFilters = ({
  activeTab,
  supplierFilterMode,
}: UsePublicMarketFiltersProps) => {
  const [dateOptions, setDateOptions] = useState<string>('publication');
  const [externalCode, setExternalCode] = useState<string>('');
  const [selectedBuyer, setSelectedBuyer] = useState<string>('');
  const [selectedSupplier, setSelectedSupplier] = useState<string>('');
  const [status, setStatus] = useState<{ value: string; label: string }[]>([]);
  const [types, setTypes] = useState<
    { value: TenderType | PurchaseOrderType; label: string }[]
  >([]);
  const [initialDateStr, setInitialDateStr] = useState<string | null>(
    dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
  );
  const [finalDateStr, setFinalDateStr] = useState<string | null>(
    dayjs().format('YYYY-MM-DD'),
  );

  const tenderTypeOptions = Object.values(TenderType);
  const purchaseOrderTypeOptions = Object.values(PurchaseOrderType);

  const statusOptions = useMemo(
    () =>
      activeTab === 1 || (activeTab === 3 && supplierFilterMode === 'tender')
        ? TENDER_STATES
        : activeTab === 2 ||
            (activeTab === 3 && supplierFilterMode === 'purchaseOrder')
          ? PURCHASE_ORDER_STATES
          : [],
    [activeTab, supplierFilterMode],
  );

  const showDateOptions = useMemo(
    () =>
      activeTab === 1 || (activeTab === 3 && supplierFilterMode === 'tender'),
    [activeTab, supplierFilterMode],
  );
  const showSupplierFilter = useMemo(
    () =>
      activeTab === 2 ||
      (activeTab === 3 && supplierFilterMode === 'purchaseOrder'),
    [activeTab, supplierFilterMode],
  );
  const showStatusFilter = useMemo(() => activeTab !== 0, [activeTab]);
  const isTenderMode = useMemo(
    () =>
      activeTab === 1 || (activeTab === 3 && supplierFilterMode === 'tender'),
    [activeTab, supplierFilterMode],
  );
  const isSummaryMode = useMemo(() => activeTab === 0, [activeTab]);

  const options = useMemo(() => {
    return isSummaryMode
      ? tenderTypeOptions.map((t) => ({ value: t, label: t }))
      : isTenderMode
        ? tenderTypeOptions.map((t) => ({ value: t, label: t }))
        : purchaseOrderTypeOptions.map((t) => ({ value: t, label: t }));
  }, [
    isSummaryMode,
    isTenderMode,
    tenderTypeOptions,
    purchaseOrderTypeOptions,
  ]);

  const allFilters = useMemo<Array<GlobalFilter & { show?: boolean }>>(
    () => [
      {
        type: 'date',
        label: 'Fecha inicio',
        dateKey: 'startDate',
        value: initialDateStr,
        setter: setInitialDateStr,
        show: true,
      },
      {
        type: 'date',
        label: 'Fecha fin',
        dateKey: 'endDate',
        value: finalDateStr,
        setter: setFinalDateStr,
        show: true,
      },
      {
        type: 'select',
        label: 'Fecha',
        value: dateOptions,
        setter: setDateOptions,
        options: [
          { value: 'publication', label: 'Publicación' },
          { value: 'adjudication', label: 'Adjudicación' },
        ],
        show: showDateOptions,
      },
      {
        type: 'text',
        label: 'Código',
        value: externalCode,
        setter: setExternalCode,
        show: true,
      },
      {
        type: 'multiple',
        label: 'Tipo',
        value: types,
        setter: setTypes,
        options,
        show: true,
      },
      {
        type: 'autocomplete',
        label: 'Mandante',
        key: 'buyer',
        value: selectedBuyer,
        setter: setSelectedBuyer,
        show: true,
      },
      {
        type: 'autocomplete',
        label: 'Proveedor',
        key: 'supplier',
        value: selectedSupplier,
        setter: setSelectedSupplier,
        show: showSupplierFilter,
      },
      {
        type: 'multiple',
        label: 'Estado',
        value: status,
        setter: setStatus,
        options: statusOptions.map((s) => ({
          value: String(s.value),
          label: s.label,
        })),
        show: showStatusFilter,
      },
    ],
    [
      initialDateStr,
      setInitialDateStr,
      finalDateStr,
      setFinalDateStr,
      dateOptions,
      setDateOptions,
      externalCode,
      setExternalCode,
      types,
      setTypes,
      selectedBuyer,
      setSelectedBuyer,
      selectedSupplier,
      setSelectedSupplier,
      status,
      setStatus,
      statusOptions,
      showDateOptions,
      showSupplierFilter,
      showStatusFilter,
      isSummaryMode,
      isTenderMode,
      tenderTypeOptions,
      purchaseOrderTypeOptions,
    ],
  );

  const filters = useMemo(
    () => allFilters.filter((f) => f.show !== false) as GlobalFilter[],
    [allFilters],
  );

  const resetFilters = () => {
    setInitialDateStr(dayjs().subtract(30, 'day').format('YYYY-MM-DD'));
    setFinalDateStr(dayjs().format('YYYY-MM-DD'));
    setDateOptions('publication');
    setExternalCode('');
    setSelectedBuyer('');
    setSelectedSupplier('');
    setStatus([]);
    setTypes([]);
  };

  return {
    filters,
    initialDateStr,
    setInitialDateStr,
    finalDateStr,
    setFinalDateStr,
    dateOptions,
    setDateOptions,
    externalCode,
    setExternalCode,
    selectedBuyer,
    setSelectedBuyer,
    selectedSupplier,
    setSelectedSupplier,
    status,
    setStatus,
    types,
    setTypes,
    resetFilters,
  };
};
